import { Command } from "commander";
import { logErr, logInfo, logOk, S } from "../helpers.ts";

async function runDebugQuery(
  query: "tools" | "connectors" | "mcp" | "config" | "state",
  parentOpts: { url: string },
  args?: Record<string, unknown>,
): Promise<void> {
  const { WebSocketClientTransport } = await import("@skaile/workspaces/transport/ws/client");

  const client = new WebSocketClientTransport({
    url: parentOpts.url,
    reconnect: { maxAttempts: 0 },
  });

  try {
    await client.connect();
  } catch (err) {
    logErr(
      `Could not connect to ${parentOpts.url}: ${err instanceof Error ? err.message : String(err)}`,
    );
    process.exitCode = 1;
    return;
  }

  await new Promise<void>((resolve) => {
    const timeout = setTimeout(() => {
      logErr("Timeout: no debug_result received within 5s");
      process.exitCode = 1;
      resolve();
    }, 5_000);

    client.onMessage((event) => {
      if (event.type !== "debug_result") return;
      clearTimeout(timeout);
      printDebugResult(query, event.data);
      resolve();
    });

    client.send({ type: "debug", query, args });
  });

  await client.disconnect();
}

function printDebugResult(query: string, data: unknown): void {
  console.log();
  console.log(S.heading(`  debug: ${query}`));
  console.log();
  if (data === null || data === undefined) {
    logInfo("(no data)");
    return;
  }
  if (typeof data === "object" && !Array.isArray(data)) {
    const obj = data as Record<string, unknown>;
    if ("error" in obj) {
      logErr(String(obj.error));
      return;
    }
  }
  console.log(JSON.stringify(data, null, 2));
  console.log();
  logOk("done");
}

/**
 * Creates the `skaile debug` command group.
 *
 * Connects to a running agent server via WebSocket and queries its internal
 * state. Subcommands: `tools`, `connectors`, `mcp`, `config`, `state [store]`.
 * Defaults to `ws://localhost:8080`.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/misc#debug
 */
export function makeDebugCommand(): Command {
  const cmd = new Command("debug")
    .description("Inspect a running agent container's internal state")
    .option("--url <url>", "WebSocket server URL", "ws://localhost:8080");

  cmd
    .command("tools")
    .description("List all tools the SDK sees (MCP + native)")
    .action(async (_, parent) => {
      await runDebugQuery("tools", parent.parent.opts());
    });

  cmd
    .command("connectors")
    .description("List registered connectors and their state")
    .action(async (_, parent) => {
      await runDebugQuery("connectors", parent.parent.opts());
    });

  cmd
    .command("mcp")
    .description("List MCP servers and their registered tools")
    .action(async (_, parent) => {
      await runDebugQuery("mcp", parent.parent.opts());
    });

  cmd
    .command("config")
    .description("Dump driver config (API keys redacted)")
    .action(async (_, parent) => {
      await runDebugQuery("config", parent.parent.opts());
    });

  cmd
    .command("state [store]")
    .description("Read a shared state store snapshot (or list all stores)")
    .action(async (store, _, parent) => {
      await runDebugQuery("state", parent.parent.opts(), store ? { store } : undefined);
    });

  return cmd;
}
